import React, {useRef, useLayoutEffect} from 'react';
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import {choicesColorsCluster, matrixAnswersDataToPercent} from "../../../../utils/ChartsUtils";

am4core.useTheme(am4themes_animated);

function ClusteredBarChartComponent(props) {
    const chartRef = useRef(null);

    let labelFlag = 0;
    const parsedData = matrixAnswersDataToPercent(props.answersData, props.questionSelects, props.isMultipleMatrix);
    const clusterNo = parsedData.length;

    function updateCurrentChartInfo(chart) {
        let newChartInfo = {
            chartText: props.questionTitle,
            chart: chart
        };
        props.updateChartInfo && props.updateChartInfo(props.chartIndex, newChartInfo);
    }

    function setChartData(chart, data) {
        chart.data = data;
    }

    function setChartSettings(chart) {
        chart.responsive.enabled = true;
        chart.maskBullets = false;
        if (props.isMobile) {
            chart.paddingTop = 20;
            chart.paddingBottom = 20;
            chart.paddingLeft = 30;
            chart.paddingRight = 30;
        } else {
            chart.paddingLeft = 100;
            chart.paddingRight = 200;
        }
        chart.width = props.isMobile ? am4core.percent(100) : props.chartConfig.width;
        chart.height = props.chartConfig.height;
        chart.cursor = new am4charts.XYCursor();
        chart.cursor.lineX.disabled = true;
        chart.cursor.lineY.disabled = true;
        chart.cursor.behavior = "none";

        chart.legend = new am4charts.Legend();
        chart.legend.position = "bottom";
        chart.legend.itemContainers.template.paddingTop = 30;
    }

    function setColumnsSettings(chart) {

        let categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis());
        categoryAxis.dataFields.category = "choiceLabel";
        categoryAxis.dataFields.data = "choiceLabelWithCount";
        categoryAxis.numberFormatter.numberFormat = "#";
        categoryAxis.renderer.inversed = true;
        categoryAxis.strokeOpacity = 0;
        categoryAxis.renderer.grid.template.location = 0;
        categoryAxis.renderer.cellStartLocation = 0.15;
        categoryAxis.renderer.cellEndLocation = 1;
        categoryAxis.renderer.grid.template.disabled = true;
        categoryAxis.renderer.tooltip.dx = -40;
        if (props.isMobile) {
            categoryAxis.renderer.inside = true;
            categoryAxis.renderer.labels.template.verticalCenter = "middle";
            categoryAxis.renderer.labels.template.dx = -10;
            categoryAxis.renderer.labels.template.adapter.add("dy", () => {
                const gridHeight = chartRef.current.plotContainer.measuredHeight/clusterNo;
                const labelYOffset = gridHeight * -0.325 - 16 * gridHeight/200;
                return labelYOffset;
            });
            categoryAxis.renderer.labels.template.adapter.add("text", function (text) {
                return "{data}";
            });
        } else {
            categoryAxis.renderer.minWidth = 120;
        }
        categoryAxis.renderer.labels.template.fontSize = props.isMobile ? 12 : 10;
        categoryAxis.renderer.labels.template.fontWeight = "bold";
        categoryAxis.renderer.labels.template.wrap = ! props.isMobile;
        categoryAxis.renderer.labels.template.textAlign = "end";
        categoryAxis.renderer.labels.template.maxWidth = props.isMobile ? props.chartConfig.width : 100;
        categoryAxis.renderer.grid.template.strokeOpacity = 0;

        let valueAxis = chart.xAxes.push(new am4charts.ValueAxis());
        valueAxis.renderer.opposite = false;
        valueAxis.renderer.grid.template.disabled = true;
        valueAxis.min = 0;
        valueAxis.max = 100;
        valueAxis.calculateTotals = true;
        valueAxis.strictMinMax = true;
        valueAxis.renderer.labels.template.adapter.add("text", function(text) {
            return text + "%";
        });

        if (! props.isMobile) {
            var invisibleContainer = chart.chartContainer.createChild(am4core.Container);
            invisibleContainer.layout = "static";
            invisibleContainer.toBack();
            invisibleContainer.paddingTop = 50;

            var axisTitle = chart.plotContainer.createChild(am4core.Label);
            axisTitle.id = "text_choices_label";
            axisTitle.text = "VALUE";
            axisTitle.fontWeight = 200;
            axisTitle.dx = -80;
            axisTitle.dy = -50;
            axisTitle.zIndex = "5000";

            var dateTitle = chart.plotContainer.createChild(am4core.Label);
            dateTitle.id = "percent_responses_label";
            dateTitle.text = "RESPONSES";
            dateTitle.fontWeight = 200;
            dateTitle.dy = -50;
            dateTitle.zIndex = "5000";
        }

        chart.colors.list = choicesColorsCluster.map(c_hex => am4core.color(c_hex));

        function createSeries(field, name) {
            let series = chart.series.push(new am4charts.ColumnSeries());
            series.dataFields.valueX = field;
            series.dataFields.categoryY = "choiceLabel";
            series.name = name;
            series.columns.template.tooltipText = "{name}: [bold]{valueX}[/]";
            series.columns.template.strokeOpacity = 0;
            series.columns.template.background.fill = am4core.color("#F7F7F7");
            series.columns.template.column.cornerRadius(0, 60, 0, 60);
            series.columns.template.width = am4core.percent(100);
            series.columns.template.maxHeight = 50;
            series.sequencedInterpolation = true;
            series.columns.template.adapter.add('tooltipX', tooltipXPositionAdapter);

            let completingSeries = chart.series.push(new am4charts.ColumnSeries);
            completingSeries.dataFields.valueX = field + "remaining";
            completingSeries.dataFields.categoryY = "choiceLabel";
            completingSeries.stacked = true;
            completingSeries.hiddenInLegend = true;
            completingSeries.calculatePercent = true;
            completingSeries.hide();
			
            let categoryLabel = series.bullets.push(new am4charts.LabelBullet());
            categoryLabel.label.text = "[bold] {valueX}% [/]";
            categoryLabel.label.fontSize = 12;
            categoryLabel.label.fontFamily = "Montserrat, sans-serif";
            categoryLabel.locationX = 0.5;
            categoryLabel.label.valign = "middle";
            categoryLabel.label.fill = am4core.color("#000");
            categoryLabel.label.truncate = false;
            categoryLabel.label.hideOversized = false;
            categoryLabel.label.horizontalCenter = "left";
            categoryLabel.locationX = 1;
            categoryLabel.dx = 3;
            chart.maskBullets = false
			
            let columnRemainingTemplate = completingSeries.columns.template;
            columnRemainingTemplate.height = am4core.percent(50);
            columnRemainingTemplate.maxHeight = 50;
            columnRemainingTemplate.maxWidth = 50;
            columnRemainingTemplate.column.cornerRadius(10, 10, 10, 10);
            columnRemainingTemplate.strokeOpacity = 0;
            columnRemainingTemplate.background.fill = am4core.color("#F7F7F7");
            columnRemainingTemplate.fill = am4core.color("#F7F7F7");
            if (!props.isMobile && labelFlag === 0) {
                labelFlag = 1;
                let valueLabel = completingSeries.bullets.push(new am4charts.LabelBullet());
                //valueLabel.locationX = 0;
                valueLabel.label.fontSize = 15;
                valueLabel.label.truncate = false;
                valueLabel.label.hideOversized = false;
                valueLabel.label.horizontalCenter = "right";
                valueLabel.align = "right";
                valueLabel.label.dx = 50;

                valueLabel.adapter.add("x", function (x) {
                    // Align RESPONSES with the "valueLabel" LabelBullet
                    let label = chartRef.current.plotContainer.children.values.find((elem) => {
                        return elem.id === "percent_responses_label"
                    });
                    label.x = x * (chart.xAxes.values[0].max / 100);

                    return x * (chart.xAxes.values[0].max / 100);
                });
                valueLabel.label.adapter.add("text", function (text, target) {
                    const entry = parsedData.find(e => e.choiceLabel === target.dataItem.categories.categoryY);
                    if (typeof entry.total === 'undefined') {
                        return "N/A"
                    } else {
                        return entry.total;
                    }
                });
            }
        }

        function capitalizeFirstLetter(string) {
            return string.charAt(0).toUpperCase() + string.slice(1);
        }

        function tooltipXPositionAdapter(value, target) {
            return (target.dataItem ? target.bbox.width : 0);
        }

        if (parsedData) {
            Object.entries(parsedData[0]).forEach(([key, val]) => {
                if (!key.includes('choiceLabel') && key !== 'total' && !key.includes('remaining'))
                    createSeries(key, capitalizeFirstLetter(key));
            });

        }
    }

    useLayoutEffect(() => {
        let x = am4core.create("clustered_barchart_" + props.questionId, am4charts.XYChart);

        setChartSettings(x);
        setColumnsSettings(x);
        setChartData(x, parsedData);

        chartRef.current = x;
        updateCurrentChartInfo(chartRef.current);
        return () => {
            x.dispose();
        };
    }, []);

    useLayoutEffect(() => {
        if (! props.isMobile) {
            chartRef.current.width = props.chartConfig.width;
        }
        chartRef.current.height = props.chartConfig.height;
        chartRef.current.xAxes.values[0].max = props.chartConfig.scale;
        chartRef.current.yAxes.values[0].renderer.labels.template.maxWidth = props.chartConfig.marginLeft + 250;
        updateCurrentChartInfo(chartRef.current);
    }, [props.chartConfig]);


    const styleClusteredBarchartDiv = {width: props.chartConfig.width, height: props.chartConfig.height};
    if (props.isMobile) {
        styleClusteredBarchartDiv.width = "auto";
    }

    function compare( a, b ) {
        if(props.sortingOptions.option === 'desc') {
            if (parseFloat(a[props.sortingOptions.sortingColumn]) > parseFloat(b[props.sortingOptions.sortingColumn])) {
                return -1;
            }
            if (parseFloat(a[props.sortingOptions.sortingColumn]) < parseFloat(b[props.sortingOptions.sortingColumn])) {
                return 1;
            }
        }else{
            if (parseFloat(a[props.sortingOptions.sortingColumn]) < parseFloat(b[props.sortingOptions.sortingColumn])) {
                return -1;
            }
            if (parseFloat(a[props.sortingOptions.sortingColumn]) > parseFloat(b[props.sortingOptions.sortingColumn])) {
                return 1;
            }
        }
        return 0;
    }

    function sortData(){
        chartRef.current.data = chartRef.current.data.sort(compare);
    }


    useLayoutEffect( () => {

        if(props.sortingOptions && props.sortingOptions.option === 'revert'){
            chartRef.current.data = matrixAnswersDataToPercent(props.answersData, props.questionSelects, props.isMultipleMatrix);
            return;
        }

        if(props.sortingOptions !== null) {
            sortData();
        }
        updateCurrentChartInfo(chartRef.current);
    },[props.sortingOptions]);




    return (
        <div className="centered-chart" id={"clustered_barchart_" + props.questionId} style={styleClusteredBarchartDiv}></div>
    );
}

export default ClusteredBarChartComponent;